import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Layers, BrainCircuit, Trophy, Zap, BookOpen, Wand2, Check,
  MessagesSquare, ShoppingBag, Users, Camera, Lightbulb, LayoutDashboard, ChevronDown } from "lucide-react";
import axios from "axios";
import BrainLogo from "@/components/BrainLogo";
import LegalLinks from "@/components/LegalLinks";
import ThemeToggle from "@/components/ThemeToggle";
import FreeModeBanner from "@/components/FreeModeBanner";
import Seo from "@/components/Seo";
import JsonLd from "@/components/JsonLd";
import { useAuth } from "@/lib/auth";

const FAQS = [
  { q: "What is Studivault?",
    a: "Studivault is an AI study tool that turns any PDF, document, lecture, video or pasted text into structured notes, summaries, flashcards, quizzes, a glossary and predicted exam questions — then keeps you sharp with spaced-repetition reviews, XP, gems and streaks." },
  { q: "Is Studivault free to use?",
    a: "Yes. You can create your first 5 studies for free, forever, with no credit card required. Paid Plus and Pro plans unlock audio/video/YouTube sources, exam mode with OCR grading, unlimited studies and an ad-free experience." },
  { q: "What file types and sources can I turn into study material?",
    a: "PDFs and pasted text on every plan; audio recordings, video files and YouTube links on Plus and Pro. Studivault transcribes and structures all of them into notes, flashcards, quizzes and exam questions automatically." },
  { q: "How does Studivault help me actually remember things?",
    a: "Every flashcard is scheduled with the SM-2 spaced-repetition algorithm, so you review each card right before you'd forget it. Daily reviews, adaptive quizzes and an AI tutor reinforce weak spots over time." },
  { q: "Does it work for exams like JEE, NEET, UPSC, SAT, GRE or USMLE?",
    a: "Yes. Studivault generates predicted exam questions and supports 13+ major exams. Pro adds timed mock exams where you can photograph handwritten answers for instant AI grading." },
  { q: "What is the gem economy and community marketplace?",
    a: "You earn gems through daily logins and activity, and can spend them to import premium study packs from the community marketplace. Publish your own packs and earn gems whenever other students import them." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.06 },
  }),
};

export default function Landing() {
  const { user } = useAuth();
  const [freeMode, setFreeMode] = useState(false);
  const [livePlans, setLivePlans] = useState([]);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/config/public`)
      .then((r) => setFreeMode(!!r.data?.free_mode)).catch(() => {});
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/billing/plans`)
      .then((r) => setLivePlans(r.data?.plans || [])).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-[#0A0A0B] text-[#F4F4F5] overflow-x-hidden">
      <Seo title="AI Study Tool — notes, flashcards, quizzes & exams"
        description="Less cramming, more leveling up. Studivault turns any PDF, video or lecture into notes, flashcards, quizzes and predicted exams — and rewards you with XP, gems and streaks while you actually remember everything."
        path="/" />
      <JsonLd id="faq-jsonld" data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }} />
      {/* Nav */}
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-[1320px] mx-auto px-4 md:px-8 h-[56px] flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <BrainLogo size={28} />
            <span className="font-semibold tracking-tight text-[16px]">Studivault</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1 text-[13px] font-medium text-[#B0AEBC]">
            <a href="#features" className="px-3 py-1.5 rounded-full hover:bg-white/10 hover:text-white">Features</a>
            <a href="#how" className="px-3 py-1.5 rounded-full hover:bg-white/10 hover:text-white">How it works</a>
            <a href="#pricing" className="px-3 py-1.5 rounded-full hover:bg-white/10 hover:text-white">Pricing</a>
            <a href="#faq" className="px-3 py-1.5 rounded-full hover:bg-white/10 hover:text-white">FAQ</a>
            <Link to="/explore" className="px-3 py-1.5 rounded-full hover:bg-white/10 hover:text-white" data-testid="landing-explore-link">Explore</Link>
          </nav>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            <ThemeToggle />
            {user ? (
              <Link to="/app" data-testid="landing-dashboard-btn">
                <button className="btn btn-primary flex items-center gap-1.5"><LayoutDashboard size={14} /> <span className="hidden sm:inline">Open app</span></button>
              </Link>
            ) : (
              <>
                <Link to="/login" className="btn btn-ghost hidden sm:inline-flex" data-testid="landing-login-btn">Sign in</Link>
                <Link to="/signup">
                  <button className="btn btn-primary" data-testid="landing-signup-btn">Get started</button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative px-5 md:px-8 pt-20 md:pt-28 pb-20 mesh-bg">
        <div className="absolute inset-0 grid-lines opacity-40 [mask-image:radial-gradient(ellipse_at_top,black_30%,transparent_70%)] pointer-events-none" />
        <div className="max-w-[1320px] mx-auto text-center relative">
          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[12px] font-medium mb-7"
          >
            <Sparkles size={12} className="text-[#B8A4FF]" /> AI that actually retains
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="show"
            custom={1}
            variants={fadeUp}
            className="display-1 max-w-5xl mx-auto"
          >
            Less cramming.
            <br />
            <span className="grad-text">More leveling up.</span>
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="show"
            custom={2}
            variants={fadeUp}
            className="body-lg text-[#B0AEBC] max-w-2xl mx-auto mt-7"
          >
            Drop a PDF or paste any text. Studivault turns it into beautifully structured notes,
            smart flashcards, and an adaptive quiz — then keeps you sharp with spaced repetition.
          </motion.p>

          <motion.div
            initial="hidden"
            animate="show"
            custom={3}
            variants={fadeUp}
            className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10"
          >
            <Link to="/signup">
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                className="btn btn-primary px-7 py-3.5 text-[15px]"
                data-testid="hero-cta-signup"
              >
                Start for free <ArrowRight size={15} />
              </motion.button>
            </Link>
            <Link to="/login" className="link-arrow" data-testid="hero-cta-login">
              I have an account →
            </Link>
          </motion.div>

          <motion.p
            initial="hidden"
            animate="show"
            custom={4}
            variants={fadeUp}
            className={freeMode ? "text-[15px] font-bold mt-6 grad-text" : "text-[12px] text-[#8E8B99] mt-6"}
            data-testid="hero-pricing-caption"
          >
            {freeMode ? "✨ Everything free, for now" : "No card · Free forever for your first 5 studies"}
          </motion.p>
        </div>

        {/* Hero visual */}
        <motion.div
          initial={{ opacity: 0, y: 48 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-[1100px] mx-auto mt-20 relative"
        >
          <div className="absolute -inset-6 bg-gradient-to-br from-[#7C5CFF]/30 via-[#5AC8FA]/20 to-[#7C5CFF]/30 rounded-[28px] blur-3xl opacity-70" />
          <HeroPreview />
        </motion.div>
      </section>

      {/* Stats */}
      <section className="px-5 md:px-8 py-16 border-t border-white/5">
        <div className="max-w-[1240px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-y-8">
          {[
            { n: "8", l: "outputs per study", c: "grad-text" },
            { n: "SM-2", l: "spaced repetition", c: "grad-text-2" },
            { n: "Fast", l: "AI generation", c: "grad-text-3" },
            { n: "Major", l: "exams covered", c: "grad-text" },
          ].map(({ n, l, c }, i) => (
            <motion.div
              key={l}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.55 }}
              className="text-center"
            >
              <div className={`display-3 ${c}`}>{n}</div>
              <div className="text-[13px] text-[#B0AEBC] mt-1">{l}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features — colored tiles */}
      <section id="features" className="px-5 md:px-8 py-24">
        <div className="max-w-[1320px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <div className="eyebrow">Built to actually retain</div>
            <h2 className="display-2 mt-3 max-w-3xl mx-auto">
              Everything you need.{" "}
              <span className="text-[#8E8B99]">Nothing you don't.</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Layers, title: "Structured notes", body: "Beautiful markdown notes with callouts, definitions, and clear hierarchy — built for reading." },
              { icon: BrainCircuit, title: "Smart flashcards", body: "SM-2 spaced repetition schedules every card so nothing slips through the cracks." },
              { icon: Zap, title: "Adaptive quizzes", body: "MCQ, true/false, short answer — auto-graded with reasoning, not just keywords." },
              { icon: MessagesSquare, title: "AI Tutor", body: "Quiz Me, Socratic, Explainer & Coach modes — a personal tutor grounded in your material." },
              { icon: Camera, title: "Timed exams + OCR grading", body: "Sit full mock exams, then snap a photo of your handwritten answers for instant AI grading." },
              { icon: Lightbulb, title: "Study techniques", body: "Feynman, Blurting & SQ3R workflows that train true understanding — not just recall." },
              { icon: Users, title: "Collaborative rooms", body: "Study live with classmates in real-time shared flashcard sessions with reactions." },
              { icon: ShoppingBag, title: "Community marketplace", body: "Discover, import and publish premium study packs — earn gems when others learn from you." },
            ].map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.5 }}
                whileHover={{ y: -4 }}
                className="surface surface-hover p-6 relative overflow-hidden"
                data-testid={`feature-${i}`}
              >
                <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-[#7C5CFF]/20 blur-3xl pointer-events-none" />
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5 bg-gradient-to-br from-[#7C5CFF]/20 to-[#5AC8FA]/10 border border-white/8">
                  <f.icon size={20} strokeWidth={1.8} className="text-[#B8A4FF]" />
                </div>
                <h3 className="text-[17px] font-semibold mb-1.5 text-white">{f.title}</h3>
                <p className="text-[14px] text-[#B0AEBC] leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </div>

          {/* Gamification strip */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="surface mt-4 p-7 md:p-9 relative overflow-hidden flex flex-col md:flex-row md:items-center gap-6"
          >
            <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-[#7C5CFF]/15 blur-3xl pointer-events-none" />
            <div className="flex-1 relative">
              <div className="flex items-center gap-2 mb-2">
                <Trophy size={18} className="text-[#B8A4FF]" />
                <h3 className="text-[19px] font-semibold text-white">Studying that feels like a game</h3>
              </div>
              <p className="text-[14px] text-[#B0AEBC] leading-relaxed max-w-xl">
                Earn XP and level up, keep daily streaks alive, collect gems, climb the weekly leaderboard and
                unlock 30+ achievements. A daily login even drops a free gem in your wallet.
              </p>
            </div>
            <div className="flex gap-3 relative">
              {[{ n: "XP", l: "& levels" }, { n: "🔥", l: "streaks" }, { n: "💎", l: "gems" }].map((s, i) => (
                <div key={i} className="surface px-5 py-4 text-center min-w-[88px]">
                  <div className="text-[22px] font-bold grad-text-3">{s.n}</div>
                  <div className="text-[11px] text-[#8E8B99] mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="px-5 md:px-8 py-24 border-y border-white/5 relative">
        <div className="absolute inset-0 mesh-bg opacity-50 pointer-events-none" />
        <div className="max-w-[1240px] mx-auto relative">
          <div className="text-center mb-14">
            <div className="eyebrow">Three steps</div>
            <h2 className="display-2 mt-3">It's that simple.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-10 md:gap-8">
            {[
              { icon: Wand2, n: "01", t: "Upload", d: "Drop a PDF or paste text — anything you'd actually study." },
              { icon: Sparkles, n: "02", t: "Generate", d: "Notes, summaries, flashcards, a quiz, and a glossary — in about a minute." },
              { icon: BookOpen, n: "03", t: "Retain", d: "Daily reviews keep what you learn from slipping away." },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.6 }}
                className="text-center relative"
              >
                <div className="relative w-14 h-14 mx-auto mb-5">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#7C5CFF]/30 to-[#5AC8FA]/20 blur-xl" />
                  <div className="relative w-14 h-14 rounded-2xl flex items-center justify-center bg-white/5 border border-white/10">
                    <s.icon size={22} className="text-[#B8A4FF]" strokeWidth={1.8} />
                  </div>
                </div>
                <div className="text-[12px] font-semibold tracking-wider mb-2 text-[#B8A4FF]">{s.n}</div>
                <h3 className="title text-white">{s.t}</h3>
                <p className="text-[15px] text-[#B0AEBC] mt-2 max-w-xs mx-auto">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Exam strip */}
      <section className="px-5 md:px-8 py-16">
        <div className="max-w-[1240px] mx-auto text-center">
          <div className="eyebrow mb-5">Made for serious students</div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 text-[20px] md:text-[24px] font-semibold">
            {["JEE","NEET","UPSC","CAT","GATE","SAT","GRE","MCAT","USMLE","CFA"].map((l, i) => (
              <motion.span
                key={l}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                whileHover={{ scale: 1.12 }}
                className="text-white transition-transform"
                style={{
                  textShadow: "0 0 12px rgba(255,255,255,0.55), 0 0 28px rgba(124,92,255,0.45), 0 0 48px rgba(90,200,250,0.30)",
                }}
              >
                {l}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="px-5 md:px-8 py-24 border-t border-white/5">
        <div className="max-w-[1180px] mx-auto">
          <div className="text-center mb-14">
            <div className="eyebrow">Pricing</div>
            <h2 className="display-2 mt-3">Free to start. <span className="text-[#8E8B99]">Affordable to scale.</span></h2>
            <p className="text-[13px] text-[#8E8B99] mt-3 max-w-md mx-auto">
              Paid plans are a <span className="text-[#B0AEBC]">one-time payment for 30 days of access</span> — no subscription, no auto-renewal.
            </p>
          </div>

          <FreeModeBanner className="mb-10 max-w-2xl mx-auto" />

          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                id: "free", name: "Free", price: 0, tagline: "Get started",
                features: ["5 studies / month", "PDF + text sources", "Daily flashcard review"],
                cta: "Sign up free",
              },
              {
                id: "plus", name: "Plus", price: 699, tagline: "For serious students", popular: true,
                features: ["50 studies / month", "Audio · Video · YouTube sources", "Study techniques — Feynman, Blurting, SQ3R", "Collaborative study rooms", "No ads — distraction-free"],
                cta: "Choose Plus",
              },
              {
                id: "pro", name: "Pro", price: 1399, tagline: "For exam season",
                features: ["Everything in Plus", "Unlimited studies", "Smarter AI by default", "Exam Mode — timed mocks + OCR grading", "Lecture Mode — long recordings", "Shared Packs & marketplace", "No ads — distraction-free"],
                cta: "Choose Pro",
              },
            ].map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.55 }}
                whileHover={{ y: -4 }}
                className="surface p-7 relative overflow-hidden"
                style={p.popular ? { borderColor: "rgba(124,92,255,0.40)", boxShadow: "0 0 0 1px rgba(124,92,255,0.25), 0 24px 60px -12px rgba(0,0,0,0.6), 0 0 50px -10px rgba(124,92,255,0.40)" } : {}}
                data-testid={`landing-plan-${p.id}`}
              >
                {p.popular && (
                  <div
                    className="absolute -top-12 -right-12 w-40 h-40 rounded-full pointer-events-none"
                    style={{ background: "radial-gradient(circle, rgba(124,92,255,0.40), transparent 65%)", filter: "blur(20px)" }}
                  />
                )}
                {p.popular && <span className="absolute top-4 right-4 chip chip-purple text-[10px]">Popular</span>}
                <div className="relative">
                  <h3 className="text-[20px] font-semibold text-white">{p.name}</h3>
                  <p className="text-[13px] text-[#B0AEBC] mt-1">{p.tagline}</p>
                  <div className="mt-5 flex items-baseline gap-1">
                    <span className="text-[44px] font-bold tracking-tight text-white tabular-nums">₹{(livePlans.find((x) => x.id === p.id)?.price_inr ?? p.price).toLocaleString("en-IN")}</span>
                    <span className="text-[13px] text-[#8E8B99]">{p.price === 0 ? "" : "/ 30 days"}</span>
                  </div>
                  <ul className="mt-6 space-y-2.5">
                    {(livePlans.find((x) => x.id === p.id)?.features || p.features).map((f, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-[13.5px] text-[#E4E4E7]">
                        <Check size={14} className="text-[#5AC8FA] mt-1 shrink-0" strokeWidth={2.5} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={p.id === "free" ? "/signup" : "/signup?plan=" + p.id}>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`btn ${p.popular ? "btn-primary" : "btn-secondary"} w-full mt-7 py-3 text-[14px]`}
                      data-testid={`landing-plan-cta-${p.id}`}
                    >
                      {p.cta} <ArrowRight size={13} />
                    </motion.button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection />

      {/* Final CTA */}
      <section className="px-5 md:px-8 py-32 mesh-bg relative">
        <div className="max-w-[800px] mx-auto text-center relative">
          <motion.h2
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="display-1"
          >
            Ready to study <span className="grad-text">better</span>?
          </motion.h2>
          <p className="body-lg text-[#B0AEBC] mt-6">
            {freeMode ? "Everything is free right now — every Plus & Pro feature unlocked. No card needed." : "Your first five studies are free, forever. No card needed."}
          </p>
          <Link to="/signup">
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              className="btn btn-primary px-7 py-3.5 text-[15px] mt-10"
              data-testid="bottom-cta-signup"
            >
              Get started — it's free <ArrowRight size={15} />
            </motion.button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/5 px-5 md:px-8 py-10">
        <div className="max-w-[1240px] mx-auto grid md:grid-cols-3 items-center gap-4 text-[12px] text-[#8E8B99]">
          <div className="flex items-center gap-2 justify-center md:justify-start">
            <BrainLogo size={20} />
            <span>© 2026 Studivault</span>
          </div>
          <LegalLinks />
          <span className="text-center md:text-right">Built for students who actually want to learn.</span>
        </div>
      </footer>
    </div>
  );
}

const FaqSection = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="px-5 md:px-8 py-24 border-t border-white/5">
      <div className="max-w-[820px] mx-auto">
        <div className="text-center mb-12">
          <div className="eyebrow">FAQ</div>
          <h2 className="display-2 mt-3">Questions, answered.</h2>
        </div>
        <div className="space-y-3" data-testid="faq-list">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="surface overflow-hidden" data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  data-testid={`faq-question-${i}`}
                  className="w-full flex items-center justify-between gap-4 text-left px-6 py-5"
                >
                  <span className="text-[15.5px] font-semibold text-white">{f.q}</span>
                  <ChevronDown
                    size={18}
                    className={`shrink-0 text-[#B8A4FF] transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>
                <motion.div
                  initial={false}
                  animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
                  transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <p className="px-6 pb-5 text-[14px] text-[#B0AEBC] leading-relaxed" data-testid={`faq-answer-${i}`}>
                    {f.a}
                  </p>
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Logo = () => (
  <motion.svg
    width="26"
    height="26"
    viewBox="0 0 26 26"
    fill="none"
    whileHover={{ rotate: 12, scale: 1.08 }}
    transition={{ type: "spring", stiffness: 300, damping: 16 }}
  >
    <defs>
      <linearGradient id="lll" x1="0" y1="0" x2="26" y2="26" gradientUnits="userSpaceOnUse">
        <stop stopColor="#7C5CFF" />
        <stop offset="0.5" stopColor="#B8A4FF" />
        <stop offset="1" stopColor="#7C5CFF" />
      </linearGradient>
    </defs>
    <rect width="26" height="26" rx="7" fill="url(#lll)" />
    <path
      d="M18 9.5C17 8.2 15.3 7.5 13.5 7.5c-3.6 0-6 2.7-6 5.5s2.4 5.5 6 5.5c1.8 0 3.5-.7 4.5-2"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      fill="none"
    />
  </motion.svg>
);

const HeroPreview = () => (
  <div className="surface relative p-2 overflow-hidden" style={{ boxShadow: "0 30px 80px -20px rgba(124,92,255,0.45), 0 0 0 1px rgba(255,255,255,0.05)" }}>
    <div className="rounded-2xl p-6 md:p-8 bg-gradient-to-br from-[#0E0E12] to-[#0A0A0B] border border-white/5">
      <div className="flex items-center gap-1.5 mb-6">
        <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
        <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
        <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
      </div>
      <div className="grid md:grid-cols-3 gap-5">
        <div className="md:col-span-2 space-y-4">
          <span className="chip chip-blue">DNA Replication · Biology</span>
          <h3 className="text-[24px] font-semibold tracking-tight text-white">
            Semi-conservative <span className="grad-text">replication</span>
          </h3>
          <p className="text-[14px] text-[#B0AEBC] leading-relaxed">
            DNA replication produces two identical molecules from one. Each new strand
            pairs with an original template strand…
          </p>
          <div className="bg-gradient-to-r from-[#7C5CFF]/15 to-transparent border-l-2 border-[#7C5CFF] rounded-r-lg px-4 py-3">
            <div className="text-[11px] uppercase tracking-wider text-[#B8A4FF] font-bold">Definition</div>
            <div className="text-[14px] mt-0.5 text-[#E4E4E7]">Helicase: an enzyme that unwinds the double helix.</div>
          </div>
        </div>
        <div className="space-y-3">
          <div className="tile-green rounded-2xl p-4">
            <div className="text-[10px] uppercase tracking-wider text-[#B0AEBC] mb-2 font-bold">Mastery</div>
            <div className="text-[28px] font-bold tabular-nums grad-text-2">87%</div>
            <div className="h-1.5 progress-track mt-2">
              <div className="progress-fill-green h-full rounded-full" style={{ width: "87%" }} />
            </div>
          </div>
          <div className="tile-purple rounded-2xl p-4">
            <div className="text-[10px] uppercase tracking-wider text-[#B0AEBC] mb-2 font-bold">Cards due</div>
            <div className="text-[28px] font-bold tabular-nums text-[#B8A4FF]">12</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);
